import { useUSDCMetadata } from '@components/Web3/UserOpTxModal/usdc/qr-fund/useUSDCAddress'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { TSigner, useBaseWrites } from 'use-towns-client'
import { Address, encodeFunctionData, erc20Abi } from 'viem'

import { allowanceQueryKey, getUSDCAllowance } from './useUSDCAllowance'

export function useUSDCApprove({
    onSuccess,
    onError,
}: {
    onSuccess?: () => void
    onError?: (error: Error) => void
} = {}) {
    const { address: usdcTokenAddress } = useUSDCMetadata()
    const queryClient = useQueryClient()
    const { baseTransactor, userOpsInstance } = useBaseWrites()
    return useMutation({
        mutationFn: async ({
            spenderAddress,
            amount,
            signer,
        }: {
            spenderAddress: Address
            amount: bigint
            signer?: TSigner
        }) => {
            if (!signer) {
                throw new Error('Signer not available')
            }
            if (!usdcTokenAddress) {
                throw new Error('USDC token address not available')
            }

            const tx = await signer.sendTransaction({
                to: usdcTokenAddress,
                data: encodeFunctionData({
                    abi: erc20Abi,
                    functionName: 'approve',
                    args: [spenderAddress, amount],
                }),
            })

            // wait for 2 confirmations to be safe
            const receipt = await baseTransactor.spaceDapp.provider.waitForTransaction(tx.hash, 2)
            if (!receipt) {
                throw new Error('Transaction failed')
            }
            return {
                walletAddress: await signer.getAddress(),
                receipt,
            }
        },
        onSuccess: async (data, variables) => {
            const qk = allowanceQueryKey(
                data.walletAddress as `0x${string}`,
                variables.spenderAddress,
                usdcTokenAddress,
            )
            try {
                const allowance = await getUSDCAllowance({
                    userOpsInstance,
                    walletAddress: data.walletAddress as `0x${string}`,
                    spenderAddress: variables.spenderAddress,
                    usdcAddress: usdcTokenAddress,
                })
                queryClient.setQueryData(qk, allowance)
            } catch (error) {
                console.error('Error getting USDC allowance', error)
            }
            onSuccess?.()
        },
        onError: (error) => {
            onError?.(error)
        },
    })
}
